/**
 * Natural Language Lexer
 * Tokenizes natural language input with fuzzy matching support
 */

import type { NLToken } from './ast.js';

/**
 * Keywords recognized by the lexer
 */
const KEYWORDS = new Set([
  'is',
  'are',
  'be',
  'been',
  'was',
  'were',
  'has',
  'have',
  'had',
  'must',
  'should',
  'can',
  'may',
  'not',
  'no',
  'at',
  'least',
  'most',
  'between',
  'outside',
  'within',
  'greater',
  'less',
  'than',
  'equal',
  'equals',
  'same',
  'after',
  'before',
  'on',
  'or',
  'checked',
  'unchecked',
  'ticked',
  'unticked',
  'selected',
  'unselected',
  'present',
  'empty',
  'blank',
  'missing',
  'provided',
  'required',
  'if',
  'then',
  'unless',
  'when',
  'and',
  'any',
  'all',
  'of',
  'contain',
  'contains',
  'starts',
  'ends',
  'with',
  'matches',
  'exactly',
  'over',
  'under',
  'more',
  'no',
  'days',
  'day',
  'hours',
  'hour',
  'minutes',
  'minute',
  'ago',
  'last',
  'from',
  'to',
  'text',
  'value',
  'date',
  'only',
  'ignore',
  'case',
  'minimum',
  'maximum',
  'min',
  'max',
  'exceed',
  'different',
  'earlier',
  'later',
  'above',
  'below',
  'character',
  'characters',
  'length',
  'long',
  'does',
  'include',
]);

/**
 * Stopwords to filter out when extracting fields
 */
const STOPWORDS = new Set([
  'the',
  'a',
  'an',
  'strictly',
  'exactly',
  'very',
  'really',
]);

/**
 * Month name -> month number (1-12)
 */
const MONTH_NAMES: Record<string, number> = {
  january: 1,
  jan: 1,
  february: 2,
  feb: 2,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  may: 5,
  june: 6,
  jun: 6,
  july: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  october: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

const MONTH_NAMES_PATTERN = Object.keys(MONTH_NAMES)
  .sort((a, b) => b.length - a.length)
  .join('|');

/**
 * Regex patterns for natural language date literals.
 * Group layouts:
 *   Pattern 1 (day-first): day=1, month=2, year=3   e.g. "5th of April 2022", "5 April 2022"
 *   Pattern 2 (month-first): month=4, day=5, year=6  e.g. "April 5, 2022", "April 5th 2022"
 */
const NL_DATE_REGEX = new RegExp(
  `\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTH_NAMES_PATTERN})\\s+(\\d{4})\\b` +
    `|(${MONTH_NAMES_PATTERN})\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b`,
  'gi',
);

/**
 * Parse a natural language date literal to an ISO date string.
 * Returns null if not a valid date.
 */
export function parseNaturalDateLiteral(text: string): string | null {
  NL_DATE_REGEX.lastIndex = 0;
  const m = NL_DATE_REGEX.exec(text.trim());
  if (!m) return null;

  let day: number, month: number, year: number;

  if (m[1] !== undefined) {
    // day-first: "5th of April 2022"
    day = parseInt(m[1], 10);
    month = MONTH_NAMES[(m[2] ?? '').toLowerCase()] ?? 0;
    year = parseInt(m[3] ?? '', 10);
  } else {
    // month-first: "April 5, 2022"
    month = MONTH_NAMES[(m[4] ?? '').toLowerCase()] ?? 0;
    day = parseInt(m[5] ?? '', 10);
    year = parseInt(m[6] ?? '', 10);
  }

  if (!month || day < 1 || day > 31 || year < 1000) return null;

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Comparator phrases (multi-word), ordered longest-first so longer phrases match first
 */
const COMPARATOR_PHRASES = [
  'greater than or equal to',
  'less than or equal to',
  'must be at least',
  'must be greater than',
  'must not exceed',
  'no more than',
  'no less than',
  'no later than',
  'no earlier than',
  'is at least',
  'is greater than',
  'is less than',
  'is at most',
  'on or after',
  'on or before',
  'same or after',
  'same or before',
  'same as',
  'not equal to',
  'different from',
  'at least',
  'at most',
  'greater than',
  'less than',
  'more than',
];

/**
 * Normalize text for fuzzy matching
 */
export function normalize(text: string): string {
  return text.toLowerCase().replace(/['']/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Calculate similarity score between two strings (0-1)
 * Uses Levenshtein-like approach with word boundaries
 */
export function similarity(s1: string, s2: string): number {
  const n1 = normalize(s1);
  const n2 = normalize(s2);

  if (n1 === n2) return 1.0;

  const words1 = n1.split(' ');
  const words2 = n2.split(' ');

  let matches = 0;
  for (const w1 of words1) {
    if (words2.includes(w1)) matches++;
  }

  const wordSimilarity = matches / Math.max(words1.length, words2.length);

  const charSimilarity =
    1 - levenshteinDistance(n1, n2) / Math.max(n1.length, n2.length);

  return wordSimilarity * 0.7 + charSimilarity * 0.3;
}

/**
 * Levenshtein distance between two strings
 */
function levenshteinDistance(s1: string, s2: string): number {
  const len1 = s1.length;
  const len2 = s2.length;

  if (len1 === 0) return len2;
  if (len2 === 0) return len1;

  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Every index below is bounded by the matrix the loop just built, so the
  // fallbacks stand in for rows and cells that cannot be missing.
  const rowAt = (i: number): number[] => matrix[i] ?? [];

  for (let i = 0; i <= len1; i++) rowAt(i)[0] = i;
  for (let j = 0; j <= len2; j++) rowAt(0)[j] = j;

  for (let i = 1; i <= len1; i++) {
    const row = rowAt(i);
    const previousRow = rowAt(i - 1);
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      row[j] = Math.min(
        (previousRow[j] ?? 0) + 1,
        (row[j - 1] ?? 0) + 1,
        (previousRow[j - 1] ?? 0) + cost,
      );
    }
  }

  return rowAt(len1)[len2] ?? 0;
}

/**
 * Tokenize natural language input
 */
export function tokenize(input: string): NLToken[] {
  const tokens: NLToken[] = [];
  const normalized = normalize(input);
  let position = 0;
  const _i = 0;

  // Preserve quoted text as one token, including whitespace and escaped quotes.
  for (let start = 0; start < input.length; start++) {
    const quote = input[start];
    if (quote !== '"' && quote !== "'") continue;
    let end = start + 1;
    let decoded = '';
    while (end < input.length && input[end] !== quote) {
      if (input[end] === '\\' && end + 1 < input.length) {
        const escaped = input[end + 1] ?? '';
        const escapes: Record<string, string> = {
          n: '\n',
          r: '\r',
          t: '\t',
          '\\': '\\',
          '"': '"',
          "'": "'",
        };
        decoded += escapes[escaped] ?? escaped;
        end += 2;
      } else {
        decoded += input[end] ?? '';
        end++;
      }
    }
    if (end < input.length) {
      const raw = input.slice(start, end + 1);
      tokens.push({
        type: 'string',
        value: raw,
        normalized: decoded,
        position: start,
      });
      start = end;
    }
  }

  // Pre-scan: find natural language date literals (e.g. "5th of April 2022", "April 5, 2022")
  // and add them as date-literal tokens before any word processing.
  {
    NL_DATE_REGEX.lastIndex = 0;
    let dateMatch: RegExpExecArray | null;
    while ((dateMatch = NL_DATE_REGEX.exec(normalized)) !== null) {
      const matchText = dateMatch[0];
      const startPos = dateMatch.index;

      let day: number, month: number, year: number;
      if (dateMatch[1] !== undefined) {
        day = parseInt(dateMatch[1], 10);
        month = MONTH_NAMES[(dateMatch[2] ?? '').toLowerCase()] ?? 0;
        year = parseInt(dateMatch[3] ?? '', 10);
      } else {
        month = MONTH_NAMES[(dateMatch[4] ?? '').toLowerCase()] ?? 0;
        day = parseInt(dateMatch[5] ?? '', 10);
        year = parseInt(dateMatch[6] ?? '', 10);
      }

      if (month && day >= 1 && day <= 31 && year >= 1000) {
        const mm = String(month).padStart(2, '0');
        const dd = String(day).padStart(2, '0');
        const isoDate = `${year}-${mm}-${dd}`;
        tokens.push({
          type: 'date-literal',
          value: matchText,
          position: startPos,
          normalized: isoDate,
          isoDate,
        });
      }
    }
  }

  for (const phrase of COMPARATOR_PHRASES) {
    const phraseNorm = normalize(phrase);
    let searchPos = 0;

    while (true) {
      const idx = normalized.indexOf(phraseNorm, searchPos);
      if (idx === -1) break;

      const before = idx === 0 || normalized[idx - 1] === ' ';
      const after =
        idx + phraseNorm.length === normalized.length ||
        normalized[idx + phraseNorm.length] === ' ';

      if (before && after) {
        tokens.push({
          type: 'comparator',
          value: input.substring(idx, idx + phraseNorm.length),
          position: idx,
          normalized: phraseNorm,
        });
      }

      searchPos = idx + 1;
    }
  }

  tokens.sort((a, b) => a.position - b.position);

  const words = input.split(/\s+/);
  position = 0;

  for (const rawWord of words) {
    const leading = rawWord.match(/^[,!?]+/)?.[0].length ?? 0;
    const word = rawWord.slice(leading).replace(/[,!?.]+$/g, '');
    if (!word) {
      position += rawWord.length;
      continue;
    }
    const rawStart = input.indexOf(rawWord, position);
    const wordStart = rawStart < 0 ? -1 : rawStart + leading;
    if (wordStart === -1) continue;

    const covered = tokens.some(
      (t) => wordStart >= t.position && wordStart < t.position + t.value.length,
    );

    if (!covered) {
      const normWord = normalize(word);

      // Determine token type - only valid identifier patterns become 'field'
      let tokenType: NLToken['type'];
      if (KEYWORDS.has(normWord)) {
        tokenType = 'keyword';
      } else if (/^-?\d+(\.\d+)?$/.test(word)) {
        tokenType = 'number';
      } else if (/^["'].*["']$/.test(word)) {
        tokenType = 'string';
      } else if (['&&', '||', 'and', 'or'].includes(normWord)) {
        tokenType = 'connector';
      } else if (['>', '<', '>=', '<=', '==', '!=', '='].includes(word)) {
        tokenType = 'operator';
      } else if (/^[a-z_][a-z0-9_.]*$/i.test(word)) {
        // Valid identifier or dotted path (e.g. value.age, root.score)
        tokenType = 'field';
      } else {
        // Unrecognised symbol (e.g. #, !, punctuation) - skip
        position = wordStart + word.length;
        continue;
      }

      tokens.push({
        value: word,
        position: wordStart,
        normalized: normWord,
        type: tokenType,
      });
    }

    position = rawStart + rawWord.length;
  }

  return tokens.sort((a, b) => a.position - b.position);
}

/**
 * Extract field names from tokens
 */
export function extractFields(tokens: NLToken[]): string[] {
  return tokens
    .filter((t, i) => {
      if (t.type === 'field' && !STOPWORDS.has(t.value.toLowerCase())) {
        return true;
      }

      if (t.value.toLowerCase() === 'value' && i < tokens.length - 1) {
        const next = tokens[i + 1];
        if (
          next &&
          (next.value === 'between' ||
            next.value === 'outside' ||
            next.value === 'is' ||
            next.value === 'must' ||
            next.value === '>' ||
            next.value === '<' ||
            next.value === '>=' ||
            next.value === '<=')
        ) {
          return true;
        }
      }
      return false;
    })
    .map((t) => t.value);
}

/**
 * Extract numbers from tokens
 */
export function extractNumbers(tokens: NLToken[]): number[] {
  return tokens
    .filter((t) => t.type === 'number')
    .map((t) => parseFloat(t.value));
}

/**
 * Find tokens by type
 */
export function findTokens(
  tokens: NLToken[],
  type: NLToken['type'],
): NLToken[] {
  return tokens.filter((t) => t.type === type);
}

/**
 * Check if tokens contain a keyword (with fuzzy matching)
 */
export function hasKeyword(
  tokens: NLToken[],
  keyword: string,
  threshold = 0.8,
): boolean {
  const norm = normalize(keyword);
  return tokens.some((t) => {
    if (t.type !== 'keyword') return false;
    const sim = similarity(t.normalized || t.value, norm);
    return sim >= threshold;
  });
}

/**
 * Check if tokens contain a comparator phrase
 */
export function hasComparator(tokens: NLToken[], comparator: string): boolean {
  const norm = normalize(comparator);
  return tokens.some(
    (t) => t.type === 'comparator' && normalize(t.value) === norm,
  );
}

/**
 * Get token sequence as normalized string
 */
export function getSequence(
  tokens: NLToken[],
  start: number,
  end: number,
): string {
  return tokens
    .slice(start, end)
    .map((t) => t.normalized || normalize(t.value))
    .join(' ');
}

/**
 * Find connectors (and/or) positions
 */
export function findConnectors(
  tokens: NLToken[],
): Array<{ index: number; type: 'and' | 'or' }> {
  const connectors: Array<{ index: number; type: 'and' | 'or' }> = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;

    const norm = token.normalized || normalize(token.value);
    if (norm === 'and' || norm === '&&') {
      connectors.push({ index: i, type: 'and' });
    } else if (norm === 'or' || norm === '||') {
      connectors.push({ index: i, type: 'or' });
    }
  }

  return connectors;
}

/**
 * Extract date-literal tokens from a token list
 */
export function extractDateLiterals(
  tokens: NLToken[],
): Array<{ isoDate: string; value: string }> {
  return tokens
    .filter((t) => t.type === 'date-literal' && t.isoDate)
    .map((t) => ({ isoDate: t.isoDate ?? '', value: t.value }));
}
